import { getVisibleText } from './textConversion';

/**
 * Helpers used by the edit flows (practice / provider) to keep update
 * payloads minimal: only fields the user actually changed are sent.
 * Untouched rows are omitted so the backend keeps its stored values
 * (it merges per key) and the audit log stays clean.
 */

export interface DiffableCustomFieldRow {
  value?: string;
  startDate?: string | null;
  endDate?: string | null;
  metadata?: Record<string, unknown> | null;
}

/** Treats null/undefined metadata as an empty object. */
export const normalizeCustomFieldMetadata = (
  metadata: Record<string, unknown> | null | undefined
): Record<string, unknown> => metadata ?? {};

const stableStringify = (value: unknown): string => {
  if (value === undefined) return 'undefined';
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value) ?? 'undefined';
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, entryValue]) => entryValue !== undefined)
    .sort(([a], [b]) => a.localeCompare(b));
  return `{${entries
    .map(([key, entryValue]) => `${key}:${stableStringify(entryValue)}`)
    .join(',')}}`;
};

/** Compares metadata objects, treating null/undefined as {}. */
export const isCustomFieldMetadataEqual = (
  candidate: Record<string, unknown> | null | undefined,
  existing: Record<string, unknown> | null | undefined,
  ignoreKeys: string[] = []
): boolean => {
  const withoutIgnored = (
    metadata: Record<string, unknown> | null | undefined
  ) =>
    Object.fromEntries(
      Object.entries(normalizeCustomFieldMetadata(metadata)).filter(
        ([key, value]) =>
          !ignoreKeys.includes(key) && value !== undefined
      )
    );

  return (
    stableStringify(withoutIgnored(candidate)) ===
    stableStringify(withoutIgnored(existing))
  );
};

/**
 * True when the row we are about to send is equivalent to the stored row.
 * Rich text rows compare visible text (so a legacy plain-text value that the
 * editor merely re-wrapped in <p> tags is not treated as an edit).
 */
export const isCustomFieldRowUnchanged = (
  candidate: DiffableCustomFieldRow | undefined,
  existing: DiffableCustomFieldRow | undefined | null,
  options: {
    isRichText?: boolean;
    visibleText?: (html: string) => string;
  } = {}
): boolean => {
  if (!candidate || !existing) return false;

  if ((candidate.startDate ?? null) !== (existing.startDate ?? null)) {
    return false;
  }
  if ((candidate.endDate ?? null) !== (existing.endDate ?? null)) {
    return false;
  }

  if (options.isRichText) {
    const toVisibleText = options.visibleText ?? getVisibleText;
    const candidateHtml = String(
      candidate.metadata?.htmlContent ?? candidate.value ?? ''
    );
    const existingHtml = String(
      existing.metadata?.htmlContent ?? existing.value ?? ''
    );
    if (existing.metadata?.htmlContent !== undefined) {
      if (candidateHtml !== existingHtml) return false;
    } else {
      if (toVisibleText(candidateHtml) !== toVisibleText(existingHtml)) return false;
    }
    return isCustomFieldMetadataEqual(
      candidate.metadata,
      existing.metadata,
      ['htmlContent']
    );
  }

  if ((candidate.value ?? '') !== (existing.value ?? '')) return false;

  return isCustomFieldMetadataEqual(
    candidate.metadata,
    existing.metadata
  );
};

/** A row that carries no information at all (nothing to store). */
export const isEmptyCustomFieldRow = (
  row: DiffableCustomFieldRow | undefined
): boolean => {
  if (!row) return true;
  if ((row.value ?? '').trim() !== '') return false;
  if ((row.startDate ?? null) !== null) return false;
  if ((row.endDate ?? null) !== null) return false;
  // htmlContent holding only empty editor markup (e.g. <p><br></p>)
  // carries no information either
  return Object.entries(
    normalizeCustomFieldMetadata(row.metadata)
  ).every(
    ([key, value]) =>
      value === undefined ||
      (key === 'htmlContent' && getVisibleText(String(value)) === '')
  );
};

const getExistingRow = (
  existing: Record<string, unknown> | null | undefined,
  fieldKey: string
): DiffableCustomFieldRow | undefined => {
  const rows = existing?.[fieldKey];
  return Array.isArray(rows)
    ? (rows[0] as DiffableCustomFieldRow | undefined)
    : undefined;
};

/**
 * Drops custom field entries that would not change anything server side:
 * rows identical to the stored row, and empty rows for fields that have
 * nothing stored yet.
 */
export const pruneUnchangedCustomFields = <
  T extends DiffableCustomFieldRow
>(
  candidates: Record<string, T[]>,
  existing: Record<string, unknown> | null | undefined,
  options: {
    isRichTextField?: (fieldKey: string) => boolean;
  } = {}
): Record<string, T[]> => {
  const pruned: Record<string, T[]> = {};

  Object.entries(candidates).forEach(([fieldKey, rows]) => {
    const candidateRow = rows?.[0];
    const existingRow = getExistingRow(existing, fieldKey);

    if (!existingRow) {
      if (isEmptyCustomFieldRow(candidateRow)) return;
      pruned[fieldKey] = rows;
      return;
    }

    if (
      isCustomFieldRowUnchanged(candidateRow, existingRow, {
        isRichText: options.isRichTextField?.(fieldKey) ?? false,
      })
    ) {
      return;
    }

    pruned[fieldKey] = rows;
  });

  return pruned;
};

/** Root (non custom field) comparison: null/undefined/'' are equivalent. */
export const isRootFieldUnchanged = (
  candidate: unknown,
  existing: unknown
): boolean => {
  const isBlank = (value: unknown) =>
    value === null || value === undefined || value === '';
  if (isBlank(candidate) && isBlank(existing)) return true;
  return candidate === existing;
};
